/**
 * NOTES-V2 N1 — Hero "Última Captura" / Fixada.
 *
 * Card grande full-width logo após o título da seção. Mostra 1 nota
 * escolhida via pickHeroNote (pinned primeiro, depois mais recente).
 *
 * Estado vazio: CTA "Falar com Nexo" + texto orientador.
 *
 * CTAs:
 *   - "Abrir em foco" (placeholder N7) → toast "Em breve"
 *   - "Fixar/Desafixar" (placeholder N6) → toast "Em breve"
 *   - "Deletar" (com confirm via window.confirm)
 *
 * Telemetria via executionTrace.js.
 */
import { useEffect, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import { Maximize2, Pin, Trash2, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import {
  pickHeroNote,
  noteTypeOf,
  formatNoteTime,
} from "@/utils/noteGrouping";
import { NoteMarkdown } from "@/utils/notesMarkdown";
import trace from "@/services/executionTrace";

export default function HeroNoteCard({
  notes,
  onDelete,
  onAskNexo,
  // PRODUTO-V1 N6/N7 — handlers reais opcionais. Quando ausentes,
  // cai no fallback toast "Em breve" (compat NOTES-V2).
  onPin,
  onOpenEditor,
}) {
  const hero = useMemo(() => pickHeroNote(notes), [notes]);
  const heroId = hero ? (hero._id || hero.id) : null;
  const type = noteTypeOf(hero);
  const Icon = type.icon;
  const isPinned = !!(hero && hero.pinned);

  // Trace 1× por mudança de heroId (não em toda re-render).
  useEffect(() => {
    if (!hero) return;
    try {
      trace.record("notes.hero.shown", {
        type: hero.type || "general",
        origin: hero.origin || "manual",
        is_pinned: isPinned,
      });
    } catch {/* */}
  }, [heroId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenFocus = useCallback(() => {
    try { trace.record("notes.hero.open_focus_clicked", { type: hero?.type || "general" }); } catch {/* */}
    if (onOpenEditor) {
      onOpenEditor(hero);
    } else {
      toast.info("Modo foco em breve");
    }
  }, [hero, onOpenEditor]);

  const handlePin = useCallback(() => {
    try { trace.record("notes.hero.pin_clicked", { is_pinned: isPinned }); } catch {/* */}
    if (onPin && hero) {
      onPin(hero, !isPinned);
      try { trace.record("notes.hero.pin_toggled", { is_pinned: !isPinned }); } catch {/* */}
    } else {
      toast.info("Fixar disponível em breve");
    }
  }, [hero, isPinned, onPin]);

  const handleDelete = useCallback(() => {
    if (!hero) return;
    if (!window.confirm("Excluir esta nota?")) return;
    try { trace.record("notes.hero.deleted", { type: hero.type || "general" }); } catch {/* */}
    onDelete?.(hero);
  }, [hero, onDelete]);

  const handleEmptyCta = useCallback(() => {
    try { trace.record("notes.hero.empty_cta", {}); } catch {/* */}
    onAskNexo?.();
  }, [onAskNexo]);

  if (!hero) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card rounded-2xl p-6 mb-5 border border-cyan-500/10"
        data-testid="hero-note-empty"
      >
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-cyan-400 mb-1">
              Última captura
            </p>
            <p className="text-base text-slate-300">
              Você ainda não anotou nada. Captura rápida: Cmd+K ou peça ao Nexo.
            </p>
          </div>
          <button
            type="button"
            onClick={handleEmptyCta}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 hover:bg-cyan-500/15 transition-all text-sm font-medium"
            data-testid="hero-note-ask-nexo"
          >
            <MessageSquare className="w-4 h-4" />
            Falar com Nexo
          </button>
        </div>
      </motion.div>
    );
  }

  const labelText = isPinned ? "Fixada" : "Última captura";

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card rounded-2xl p-6 mb-5 relative overflow-hidden"
      style={{
        borderLeft: `4px solid ${type.color}`,
        boxShadow: `0 0 30px -15px ${type.color}66`,
      }}
      data-testid="hero-note-card"
    >
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span
              className="text-xs uppercase tracking-wider font-medium"
              style={{ color: type.color }}
            >
              {labelText}
            </span>
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium uppercase tracking-wider"
              style={{ backgroundColor: `${type.color}10`, color: type.color }}
            >
              <Icon className="w-3 h-3" />
              {type.label}
            </span>
          </div>
          <div className="mb-3" data-testid="hero-note-body">
            <NoteMarkdown content={hero.content} clamp={8} />
          </div>
          <div className="flex items-center gap-2 text-[11px] text-slate-500">
            <span>criada {formatNoteTime(hero.created_at)}</span>
            {hero.origin && (
              <>
                <span>·</span>
                <span>{hero.origin === "voice" ? "via voz" : hero.origin === "agent" ? "pelo Nexo" : hero.origin}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 shrink-0">
          <motion.button
            type="button"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleOpenFocus}
            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/20 transition-all text-sm font-medium"
            data-testid="hero-note-open-focus"
          >
            <Maximize2 className="w-4 h-4" />
            Abrir em foco
          </motion.button>
          <button
            type="button"
            onClick={handlePin}
            className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-400 hover:text-amber-400 transition-colors"
            data-testid="hero-note-pin"
            aria-label={isPinned ? "Desafixar" : "Fixar"}
          >
            <Pin className="w-4 h-4" />
            {isPinned ? "Desafixar" : "Fixar"}
          </button>
          <button
            type="button"
            onClick={handleDelete}
            className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-500 hover:text-red-400 transition-colors"
            data-testid="hero-note-delete"
            aria-label="Excluir"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}
